// Import Core
import React from "react";
import styled from "styled-components";
// Import Libraries
import { GatsbyImage, getImage } from "gatsby-plugin-image";
// Import Components
import Button from "../ui/buttons/Button";
// Import Utils
import { getLinks } from "../../utils/getNavItemLinks";
import { Link } from "gatsby";
import { toSlug } from "../../utils/search/toSlug";

/* Styled Components */
const FiveCardGridStyles = styled.section`
  background: var(--white);
  padding: var(--global-padding);
  @media (max-width: 768px) {
    padding: var(--global-padding-mobile);
  }
  > div {
    display: flex;
    gap: 36px;
    @media (max-width: 1200px) {
      flex-direction: column;
      gap: 32px;
    }
    .section-title {
      border-right: 1px solid var(--grey-10);
      padding-right: 36px;
      flex: 25%;
      @media (max-width: 1200px) {
        border-right: none;
      }
      @media (max-width: 768px) {
        padding-right: 0;
        padding-bottom: 32px;
        border-bottom: 1px solid var(--grey-10);
        border-right: none;
      }
      h2 {
        padding: 16px 0 24px;
        @media (max-width: 768px) {
          padding: 12px 0 20px;
        }
      }
      p {
        font-size: var(--font-size-base); //18px
        @media (max-width: 768px) {
          font-size: var(--body-font-size); // 16px
        }
      }
      button {
        margin-top: 40px;
        @media (max-width: 768px) {
          margin-top: 24px;
        }
      }
    }
    .cards {
      display: flex;
      flex-wrap: wrap;
      flex: 75%;
      .card {
        display: flex;
        gap: 24px;
        @media (max-width: 768px) {
          gap: 20px;
        }
        .card-text {
          padding: 8px 0;
          width: 100%;
          @media (max-width: 768px) {
            padding: 0;
          }
          h4 {
            padding: 12px 0 8px;
            font-size: var(--h4-font-size);
            @media (max-width: 768px) {
              padding: 8px 0;
            }
          }
          h5 {
            padding-top: 8px;
            font-size: var(--h5-font-size);
            font-weight: 700;
          }
        }
      }
      .large-cards {
        display: flex;
        border-bottom: 1px solid var(--grey-10);
        padding-bottom: 24px;
        width: 100%;
        @media (max-width: 768px) {
          flex-direction: column;
          padding-bottom: 20px;
        }
        .card {
          width: 50%;
          @media (max-width: 1200px) {
            width: 100%;
            display: flex;
            flex-direction: column-reverse;
          }
          &:first-of-type {
            border-right: 1px solid var(--grey-10);
            padding-right: 32px;
            @media (max-width: 768px) {
              padding-right: 0;
              padding-bottom: 20px;
              border-right: none;
              border-bottom: 1px solid var(--grey-10);
            }
          }
          &:last-of-type {
            padding-left: 32px;
            @media (max-width: 768px) {
              padding-left: 0;
            }
          }
          .gatsby-image-wrapper {
            height: 192px;
            width: 200px;
            @media (max-width: 768px) {
              width: min(400px, 100%);
              height: unset;
              aspect-ratio: 1/1;
            }
          }
        }
      }
      .small-cards {
        display: flex;
        padding-top: 24px;
        width: 100%;
        @media (max-width: 768px) {
          flex-wrap: wrap;
          padding-top: 20px;
        }
        .card {
          flex: 33%;
          padding: 0 24px;
          border-right: 1px solid var(--neutral-10);
          &:first-child {
            padding: 0 24px 0 0;
          }
          &:last-child {
            padding-right: 0;
            border-right: none;
          }
          @media (max-width: 768px) {
            padding: 0 20px;
            width: 50%;
            flex-direction: column;
            padding-bottom: 20px;
            &:first-child,
            &:nth-child(2) {
              position: relative;
              margin-bottom: 20px;
              &:before {
                content: "";
                position: absolute;
                bottom: -20px;
                height: 1px;
                width: 100%;
                border-bottom: 1px solid var(--neutral-10);
              }
              &:first-child {
                padding: 0 20px 0 0;
                &:before {
                  right: 0px;
                }
              }
              &:nth-child(2) {
                padding: 0 0 0 20px;
                border-right: none;
                &:before {
                  left: 0px;
                }
              }
            }
            &:nth-child(3) {
              padding-left: 0;
            }
            &:last-child {
              padding: 0 20px 0 0;
              border-right: 1px solid var(--neutral-10);
              flex: inherit;
              width: 50%;
              margin-top: 20px;
            }
          }
          .card-text {
            @media (max-width: 768px) {
              padding-bottom: 8px;
            }
          }
          .gatsby-image-wrapper {
            height: 100px;
            width: 100px;
          }
        }
      }
      .gatsby-image-wrapper {
        img {
          object-position: center;
        }
      }
    }
  }
  .button-wrapper {
    display: flex;
    flex-direction: row;
    gap: 20px;
  }
`;

/* Render Component */
const FiveCardGrid = ({
  subheading,
  title,
  caption,
  addToAnchor,
  navItem,
  navItem2,
  people,
  personType,
}) => {
  const largeCards = people.slice(0, 2);
  const smallCards = people.slice(2, 5);

  const linkData = navItem && navItem.name && getLinks(navItem);
  const linkDatа2 = navItem2 && navItem2.name && getLinks(navItem2);
  const slugPrefix =
    personType === "artist"
      ? "/about/kirker-artists/"
      : "/about/kirker-tour-leaders/";
  return (
    <FiveCardGridStyles
      id={addToAnchor ? `anchor_${toSlug(title)}` : ""}
      className={`five-card-grid ${addToAnchor && "anchor-link"}`}
    >
      <div>
        <div className="section-title">
          {subheading && <p className="tagline italic">{subheading} </p>}
          <h2>{title}</h2>
          <p>{caption}</p>
          <div className="button-wrapper">
            {navItem && navItem.name && (
              <Link to={linkData.link}>
                <Button
                  size="md"
                  btnStyle="outline"
                  className=""
                  value={linkData.text}
                  clickHandler={false}
                  isDisabled={false}
                />
              </Link>
            )}
            {navItem2 && navItem2.name && (
              <Link to={linkDatа2.link}>
                <Button
                  size="md"
                  btnStyle="outline"
                  className=""
                  value={linkDatа2.text}
                  clickHandler={false}
                  isDisabled={false}
                />
              </Link>
            )}
          </div>
        </div>
        <div className="cards">
          <div className="large-cards">
            {largeCards &&
              largeCards.map((person, index) => (
                <a
                  href={slugPrefix + person.slug.current}
                  key={index}
                  className="card"
                >
                  <div className="image-content">
                    <GatsbyImage
                      image={getImage(person.thumbnail.asset)}
                      alt="Kirker Holidays"
                    />
                  </div>
                  <div className="card-text">
                    <p className="tagline italic">{person.jobTitle}</p>
                    <h4 className="alt">{person.title}</h4>
                    <p>
                      {person.excerpt.substring(0, 55)}
                      {person.excerpt.length > 55 ? "[...]" : ""}
                    </p>
                  </div>
                </a>
              ))}
          </div>
          <div className="small-cards">
            {smallCards &&
              smallCards.map((person, index) => (
                <a
                  href={slugPrefix + person.slug.current}
                  key={index}
                  className="small-card card"
                >
                  <div className="image-content">
                    <GatsbyImage
                      image={getImage(person.thumbnail.asset)}
                      alt="Kirker Holidays"
                    />
                  </div>
                  <div className="card-text">
                    <p className="tagline italic">{person.jobTitle}</p>
                    <h5>{person.title}</h5>
                  </div>
                </a>
              ))}
          </div>
        </div>
      </div>
    </FiveCardGridStyles>
  );
};

export default FiveCardGrid;
